import { ButtonFragment, ContentBlockFragment } from '@graphqlTypes/sitecore';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import { TrackingEvent } from '@src/adobe-launch/types';
import { withBasePath } from '@src/common/path-convert';
import { convertArticleInfo } from '@src/hooks/tracker/use-article-tracker';
import { useGlobalStore } from '@src/hooks/use-global-store';
import { jsBridge } from '@src/utils/js-bridge';
import { blockTypeLinkTo, isDeepLinkShareUrl, isShareUrl, removeCIDUrl } from '@src/utils/util';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { format, isAfter } from 'date-fns';
import toast from 'react-hot-toast';
import isSharePageStore from '@mobx/stores/isShare-store';
import { observer } from 'mobx-react-lite';

const ActionButton = ({
    content,
    contentBlock,
    currentPath,
}: {
    content: ButtonFragment;
    contentBlock?: ContentBlockFragment;
    currentPath: string;
}) => {
    // console.log('content-BTN-------', content);
    // console.log('contentBlock-contentBlock-------', contentBlock);
    // console.log('currentPath-currentPath-------', currentPath);
    const router = useRouter();
    const { settingStore } = useGlobalStore();
    const { t } = useTranslation('common');
    const isSharePage = isSharePageStore.isSharePage;
    // console.log('isSharePage----', isSharePage);

    const { calendarTitle, eventDate, eventEndDate } = content;
    // console.log(content);
    const isDeepLink = content.blockType?.value === 'DeepLink';
    const isCalendar = content.blockType?.value === 'Calendar';
    const locatSea =
        typeof window !== 'undefined' && window.location && window.location.search
            ? window.location.search
            : '';
    const generalLink = isSharePage ? '' : removeCIDUrl(`/${currentPath}/${locatSea}`);
    // const calendarEventUUIDList = JSON.parse(
    //     sessionStorage.getItem('calendarEventUUIDList') || '[]'
    // );

    const [debouncedClick, setDebouncedClick] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);

    // const generalLink = `/${currentPath}/${contentBlock.key}${location.search}`;

    // const searchParams = getSearchParams();
    // const ac = searchParams.getAll(DiscoverQueryParams.account);
    // const seg = searchParams.getAll(DiscoverQueryParams.segment);
    // const btnURL = externalLink(
    //     content.buttonRedirectionURL?.value ?? '',
    //     content.buttonHashId?.boolValue ? settingStore.wid ?? '' : '',
    //     content.buttonAc?.boolValue ? ac : [],
    //     content.buttonSeg?.boolValue ? seg : []
    // );
    // console.log('btnURL----', btnURL);
    const currentTime = new Date();
    const isShowAddCalendarIcon =
        eventDate?.dateValue &&
        eventEndDate?.dateValue &&
        eventDate?.dateValue > 0 &&
        eventEndDate?.dateValue > 0 &&
        !isAfter(currentTime, new Date(eventEndDate?.dateValue));

    const isCalendarShareLink = () => {
        if (isDeepLink) {
            return isDeepLinkShareUrl(content.deepLink?.value ?? '', true);
        }

        const shareLink =
            content.blockType?.value === 'Internal'
                ? content.internalLink?.value ?? ''
                : `${currentPath}/`;

        return isShareUrl(
            content.blockType?.value,
            shareLink,
            content.raceLink?.targetItem?.path,
            true
        );
    };

    /** add to calendar success */
    const ToastSuc = () => {
        toast.success(t('AddedToCalendar'), {
            duration: 3000,
            position: 'top-center',
            style: {
                backgroundColor: '#E2EDF6',
                color: '#000',
                fontSize: '15px',
                height: '36px',
                width: '100%',
            },
            className: 'ToastStyle',
        });
    };
    /** add to calendar error */
    const ToastErr = () => {
        toast.error(t('FailedAddToCalendar'), {
            duration: 3000,
            position: 'top-center',
            style: {
                backgroundColor: '#E2EDF6',
                color: '#000',
                fontSize: '15px',
                height: '36px',
                width: '100%',
            },
            className: 'ToastStyle',
        });
    };

    const handleCalendarClick = useCallback(async () => {
        !btnDisabled && setDebouncedClick(true); // 点击事件触发
    }, [btnDisabled]);

    useDebounce(
        () => {
            if (debouncedClick) {
                console.log('进入防抖');
                nonPageTracker(
                    {
                        eventID: TrackingEvent.AddToCalendarArticle,
                    },
                    { article: convertArticleInfo(contentBlock) }
                );
                // 执行你的逻辑
                (async () => {
                    try {
                        let dateStr = '';
                        if (eventDate?.dateValue) dateStr = format(eventDate.dateValue, 'yyyyMMdd');
                        const idStr = (contentBlock?.id ?? '') + '_' + dateStr;
                        const res = await jsBridge.addCalendarEvent(
                            calendarTitle?.value ?? '',
                            // isCalendarShareUrl(currentPath),
                            isCalendarShareLink(),
                            idStr,
                            eventDate?.dateValue ?? 0,
                            eventEndDate?.dateValue ?? 0
                        );
                        // console.log('Event added successfully:', res);
                        if (res.result) {
                            setBtnDisabled(res.result);
                            const newUUID = idStr;
                            settingStore.calendarEventUUIDList = [
                                ...settingStore.calendarEventUUIDList,
                                newUUID,
                            ];
                            ToastSuc();
                        } else {
                            ToastErr();
                        }
                    } catch (error) {
                        ToastErr();
                        console.error('Error adding event:', error);
                    }
                })();

                setDebouncedClick(false);
            }
        },
        300,
        [debouncedClick]
    );

    /** 判断是否已经添加过Calendar */
    useEffect(() => {
        if (isCalendar) {
            let dateStr = '';
            if (eventDate?.dateValue) dateStr = format(eventDate.dateValue, 'yyyyMMdd');
            const idStr = (contentBlock?.id ?? '') + '_' + dateStr;
            const calendarEventUUIDList = settingStore?.calendarEventUUIDList ?? [];
            const iconDisabled = calendarEventUUIDList.includes(idStr);
            setBtnDisabled(iconDisabled);
        }
    }, [contentBlock?.id, eventDate, isCalendar, settingStore?.calendarEventUUIDList]);

    return (
        <>
            {/* Calendar 类型 */}
            {content.buttonEnabled?.boolValue && isCalendar && isShowAddCalendarIcon && (
                <div
                    className={cn(
                        'bg-[#122C68] w-full relative text-[#fff] text-[16px]  h-[40px] leading-[40px] rounded-full text-center  px-[16px] font-medium overflow-hidden my-[16px]',
                        btnDisabled && 'bg-[#CCC] text-[#8B8B8B]'
                    )}
                    onClick={handleCalendarClick}
                >
                    <img
                        className='w-[24px] h-[24px] absolute left-[16px] top-[50%] translate-y-[-50%]'
                        src={withBasePath(
                            `/images/calendar/${btnDisabled ? 'addClaGrey' : 'addClaW'}.svg`
                        )}
                        alt=''
                        loading='lazy'
                    />
                    <div className='px-8 truncate'>{content?.buttonText?.value}</div>
                </div>
            )}

            {/* 常规类型 */}
            {content.buttonEnabled?.boolValue && !isCalendar && (
                <div
                    className='bg-[#122C68] text-[#fff] max-w-[332px] h-[44px] leading-[44px] rounded-full text-center max-md:mx-auto my-[16px] font-medium'
                    onClick={() => {
                        nonPageTracker(
                            {
                                eventID: TrackingEvent.CallToAction,
                                eventValue: content.customTag?.value,
                            },
                            { article: convertArticleInfo(contentBlock) }
                        );
                    }}
                >
                    {isDeepLink ? (
                        <a
                            href={content.deepLink?.value ?? ''}
                            target='_blank'
                            className='block w-full h-full'
                        >
                            {content?.buttonText?.value}
                        </a>
                    ) : (
                        <div
                            onClick={() => {
                                blockTypeLinkTo(
                                    content,
                                    generalLink,
                                    router,
                                    jsBridge,
                                    settingStore,
                                    'article'
                                );
                            }}
                        >
                            {content?.buttonText?.value}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default observer(ActionButton);
